import { type ReorderableItem } from '@/common/ui';
import { AASM_BAND_FILTER_DEFAULTS, hasAdjustableBandFilter } from '@/common/utils';
import {
  ScoreSetEvent,
  SignalOverflow,
  type SignalConfig,
  UserResponse
} from '@/modules/epoch/types';
import { ViewMode, type DialogSettings, type EpochState, type SettingPreset } from '../state';
import { EventType, SignalPanel, type SafeAny } from '@/common/types';
import { markRaw } from 'vue';
import { overlappingRespiratoryEventIDs } from '@/core/helpers';
import { signalAmplitudeRules } from '../../common/components/signal-graph/signalAmplitudeRules';
import { presetsFromApi, signalsListFromApi } from '../lane-height.mapper';

type EpochSettings = EpochState['settings'];

export const toggleDialogSetting = (state: EpochState, payload: keyof DialogSettings) => {
  if (state.dialog[payload] !== undefined) {
    state.dialog[payload] = !state.dialog[payload];
  }
};

const findSignalByEventType = (eventType: string, signalEventMapping: SafeAny) => {
  for (const signal in signalEventMapping) {
    if (Object.prototype.hasOwnProperty.call(signalEventMapping, signal)) {
      if (signalEventMapping[signal].includes(eventType)) {
        return signal;
      }
    }
  }
  return null;
};

export const updateEventInSignalEvents = (
  state: EpochState,
  payload: { id: string; type: string; start: string; duration: number }
) => {
  const { id, type, start, duration } = payload;
  setOverlappedRespiratoryEvents(state, payload);
  const signal = findSignalByEventType(type, state.signalEventMapping);

  if (signal && state.signalEvents[signal]) {
    const resolvedId = (state as any).eventIdMap?.[id] ?? id;

    const eventTarget = state.signalEvents[signal].find(
      (event) => event.id === id || event.id === resolvedId
    );

    if (eventTarget) {
      eventTarget.eventType = type;
      eventTarget.start = start;
      eventTarget.duration = duration;

      if (resolvedId !== id) {
        eventTarget.id = resolvedId;
      }
    }
  }
};

export const setSignalZoom = (
  state: EpochState,
  payload: { signal: string; to: [number, number] }
) => {
  const { signal, to } = payload;

  const baseMapping = state.epochData.data?.mapping?.[signal];
  const rule = signalAmplitudeRules[signal];

  if (!baseMapping) return;

  // First load - initialize from RULES, not backend mapping
  if (!state.graph.zoom[signal]) {
    // Rule-defined defaults:
    // - If rule.min/max are numbers - use them.
    // - If 'auto' - fallback to backend mapping.
    const isAuto = rule && (rule.min === 'auto' || rule.max === 'auto');

    const initialMin = rule && rule.min !== 'auto' ? Number(rule.min) : Number(baseMapping.min);
    const initialMax = rule && rule.max !== 'auto' ? Number(rule.max) : Number(baseMapping.max);

    if (isAuto) {
      state.initialAutoDefaults[signal] = {
        min: initialMin,
        max: initialMax
      };
    }

    state.graph.zoom[signal] = {
      ...baseMapping,
      min: initialMin,
      max: initialMax,
      unit: rule?.unit ?? baseMapping.unit
    } as SignalConfig;

    return;
  }

  // Subsequent calls: just apply new zoom
  state.graph.zoom[signal] = {
    ...state.graph.zoom[signal],
    min: to[0],
    max: to[1]
  } as SignalConfig;
};

// Investigate: state.epochData.data?.mapping doesn't update itself after report navigation
export const setSignalSettingsListFromAPI = (state: EpochState) => {
  const mapping = state.epochData.data?.mapping;

  if (!mapping) return;

  const selectedPreset = state.settings.presets.find(
    (preset) => preset.id === state.settings.selectedPresetId
  );
  const updatedSettings = [
    ...(selectedPreset?.signalsList || []),
    ...state.settings.signalsList
  ].reduce((acc, item) => {
    acc.set(item.name, item);

    return acc;
  }, new Map());

  state.settings.signalsList = Object.keys(mapping).reduce((acc, item, index, signals) => {
    const setting = Array.from(updatedSettings.values()).find((setting) => setting.name === item);

    if (!setting) {
      return [
        ...acc,
        {
          name: item,
          order: signals.length + index,
          visible: true,
          signalColor: 'lightBlue',
          lineColor: '#000000',
          eventColors: [],
          bandFilter: hasAdjustableBandFilter(item)
            ? { ...AASM_BAND_FILTER_DEFAULTS[item] }
            : undefined
        }
      ];
    }

    return [
      ...acc,
      {
        ...setting,
        lineColor: setting.lineColor ?? '#000000',
        eventColors: setting.eventColors ?? []
      }
    ];
  }, [] as ReorderableItem[]);

  state.settings.signalsList.sort((a, b) => a.order - b.order);
};

export const setSignalsList = (state: EpochState, items: ReorderableItem[]) => {
  state.settings.signalsList = items;
};

export const setHypnogramFullNightSignals = (state: EpochState, signals: ReorderableItem[]) => {
  state.settings.hypnogramFullNightSignals = signals;
};

export const setVisibilityOfSignal = (
  state: EpochState,
  {
    signal,
    isVisible
  }: {
    signal: string;
    isVisible: boolean;
  }
) => {
  state.settings.signalsList = state.settings.signalsList.map((item) => {
    if (item.name === signal) {
      return {
        ...item,
        visible: isVisible
      };
    }

    return item;
  });
};

export const savePreset = (state: EpochState, name: string) => {
  const id = new Date().getTime();
  const newPreset = {
    ...state.settings.presets[0],
    id: id,
    name: name
  };
  state.settings.selectedPresetId = id;
  state.settings.presets = { ...state.settings.presets, ...newPreset };
};

export const setSelectedPreset = (state: EpochState, presetId: number) => {
  state.settings.selectedPresetId = presetId;

  const preset = state.settings.presets.find((item) => item.id === presetId);

  if (!preset) {
    return;
  }

  state.settings.signalsList = preset.signalsList;
  state.settings.hypnogramFullNightSignals = preset.hypnogramFullNightSignals;
  state.settings.signalOverflow = preset.signalOverflow ?? SignalOverflow.None;
  state.settings.band = preset.band;
  state.settings.notch = preset.notch;
  state.settings.viewMode = preset.viewMode ?? ViewMode.Single;
  state.settings.artifactColor = preset.artifactColor ?? '#FF9999';
  state.settings.shortcutKeys = preset.shortcutKeys ?? [];
  state.settings.dualTimeBase = preset.dualTimeBase ?? {
    topWindowSize: 30,
    bottomWindowSize: 300,
    singleHypnogram: false
  };

  preset.signalsList.forEach((signal) => {
    state.graph.overflowVisible[signal.name] = signal.isOverflowing ?? false;
  });

  setSignalSettingsListFromAPI(state);
};

export const applySettings = (state: EpochState, localSettings: any) => {
  state.settings.signalsList = localSettings.signalsList;
  state.settings.hypnogramFullNightSignals = localSettings.hypnogramFullNightSignals;
  state.settings.signalOverflow = localSettings.signalOverflow ?? SignalOverflow.None;
  state.settings.band = localSettings.band;
  state.settings.notch = localSettings.notch;
  state.settings.viewMode = localSettings.viewMode ?? ViewMode.Single;
  state.settings.dualTimeBase = localSettings.dualTimeBase ?? {
    topWindowSize: 30,
    bottomWindowSize: 300,
    singleHypnogram: false
  };
  state.settings.artifactColor = localSettings.artifactColor ?? '#FF9999';
  state.settings.shortcutKeys = localSettings.shortcutKeys ?? [];

  localSettings.signalsList.forEach((signal: ReorderableItem) => {
    state.graph.overflowVisible[signal.name] = signal.isOverflowing ?? false;
  });
  (state.epochData as any).sets = {};
  (state.epochData as any).cacheByConfig = {};
};

export const saveEpochData = (state: EpochState, epochData: SafeAny) => {
  // Defensive: every fetch path should already wrap with markRaw before
  // committing, but guard here so an unwrapped payload from a yet-untraced
  // path never gets recursively reactified by Vue. markRaw on an already
  // markRaw'd object is a no-op; on a plain object it's an O(1) flag.
  state.epochData = epochData && typeof epochData === 'object' ? markRaw(epochData) : epochData;
};

export const insertEventInSignalEvents = (
  state: EpochState,
  payload: { id: string; type: string; start: string; duration: number }
) => {
  const { id, type, start, duration } = payload;
  setOverlappedRespiratoryEvents(state, payload);

  const signal = findSignalByEventType(type, state.signalEventMapping);
  if (signal) {
    if (!state.signalEvents[signal]) {
      state.signalEvents[signal] = [];
    }

    state.signalEvents[signal].push({
      id,
      eventType: type,
      start,
      startEpoch: +(new Date(start).getTime() / 1e11).toFixed(2),
      duration: parseFloat(duration.toFixed(2)),
      endEpoch: new Date(start).getTime() / 1000 + duration / 60
    });
  }
};

export const setOverlappedRespiratoryEvents = (
  state: EpochState,
  payload: { id: string; type: string; start?: string; duration?: number }
) => {
  let overlappingRespiratoryIDs = [] as ScoreSetEvent[];
  const { id, start, duration, type } = payload;

  if (type === EventType.WAKE) {
    overlappingRespiratoryIDs = overlappingRespiratoryEventIDs(
      id,
      start || '0',
      duration || 0,
      state.events.data || []
    ) as ScoreSetEvent[];

    state.overlappedRespiratoryEvents = [...overlappingRespiratoryIDs];
  }
};

export const updateSignalEventWithActualID = (
  state: EpochState,
  payload: { tempEventID: string; actualEventID: string; type: string }
) => {
  const { tempEventID, actualEventID, type } = payload;
  const signal = findSignalByEventType(type, state.signalEventMapping);
  setOverlappedRespiratoryEvents(state, { id: actualEventID, type });

  if (signal && state.signalEvents[signal]) {
    const eventTarget = state.signalEvents[signal].find((event) => event.id === tempEventID);
    if (eventTarget) {
      eventTarget.id = actualEventID;
    }
  }

  (state as any).eventIdMap ||= {};
  (state as any).eventIdMap[tempEventID] = actualEventID;
  (state as any).eventIdMap[actualEventID] = actualEventID;
};

export const removeEventInSignalEvents = (
  state: EpochState,
  payload: { id: string; type: string }
) => {
  const { id, type } = payload;
  const signal = findSignalByEventType(type, state.signalEventMapping);

  if (signal && state.signalEvents[signal]) {
    const resolvedId = (state as any).eventIdMap?.[id] ?? id;

    const index = state.signalEvents[signal].findIndex(
      (event) => event.id === id || event.id === resolvedId
    );

    if (index !== -1) {
      state.signalEvents[signal].splice(index, 1);
    }
  }
};

export const revertRemoveEventInSignalEvents = (state: EpochState, payload: SafeAny) => {
  const { id, eventType, start, duration } = payload;
  const signal = findSignalByEventType(eventType, state.signalEventMapping);
  if (signal) {
    const eventsOfSignal = state.signalEvents[signal] || [];
    eventsOfSignal.push({
      id,
      eventType,
      start,
      startEpoch: new Date(start).getTime() / 1000,
      duration,
      endEpoch: new Date(start).getTime() / 1000 + duration / 60
    });
    state.signalEvents[signal] = eventsOfSignal;
  }
};

export const setSignalEvents = (state: EpochState, signalEvents: SafeAny) => {
  state.signalEvents = signalEvents;
};

// Max epochs to hold in the Vuex store per epoch size.
// Evicts the oldest entry when the limit is exceeded, keeping recent epochs in memory.
// 150 entries × ~245 KB per epoch ≈ 37 MB per epoch size — well within browser limits.
const STORE_EPOCH_CACHE_LIMIT = 150;

export const setEpochDataSet = (
  state: EpochState,
  { epochSize, epoch, responseData }: { epochSize: number; epoch: number; responseData: any }
) => {
  if (!state.epochData.sets) {
    (state.epochData as any).sets = {};
  }
  if (!(state.epochData as any).sets[epochSize]) {
    (state.epochData as any).sets[epochSize] = {};
  }

  const sets = (state.epochData as any).sets[epochSize];

  // Evict oldest entry if we're at the limit — prevents unbounded growth when
  // preloading hundreds of epochs across a full-night study (~960 epochs at 30s).
  const displayedSize = (state.epochData as any).selectedEpochSize;
  const displayedEpoch = (state.epochData as any).displayedEpoch;
  const protectedKeys = new Set<string>();
  if (displayedSize === epochSize && displayedEpoch != null) {
    for (let offset = -2; offset <= 2; offset++) {
      protectedKeys.add(String(displayedEpoch + offset));
    }
  }
  const keys = Object.keys(sets);
  if (keys.length >= STORE_EPOCH_CACHE_LIMIT && !(epoch in sets)) {
    // Evict the cached epoch FARTHEST from displayedEpoch (not the lowest key).
    const focus =
      displayedSize === epochSize && Number.isFinite(Number(displayedEpoch))
        ? Number(displayedEpoch)
        : Number(epoch); // fall back to the just-added epoch as the focal point
    let farthestKey: string | undefined;
    let farthestDistance = -1;
    for (const k of keys) {
      if (protectedKeys.has(k)) continue;
      const n = Number(k);
      if (!Number.isFinite(n)) continue;
      const d = Math.abs(n - focus);
      if (d > farthestDistance) {
        farthestDistance = d;
        farthestKey = k;
      }
    }
    if (farthestKey !== undefined) {
      delete sets[farthestKey];
    }
  }

  // Mark signal data as raw to prevent Vue from creating reactive proxies
  // for the large signal arrays (7680+ samples × 16 signals). This eliminates
  // ~230ms of reactive wrapper creation and reduces GC pressure significantly.
  const data =
    responseData && typeof responseData === 'object' && !responseData.__binaryRef
      ? markRaw(responseData)
      : responseData;
  sets[epoch] = data;
};

export const clearSignalListChanges = (state: EpochState) => {
  const selectedSetting = state.settings.presets.find(
    ({ id }) => id === state.settings.selectedPresetId
  );

  if (!selectedSetting) return;

  state.settings.hypnogramFullNightSignals = selectedSetting.hypnogramFullNightSignals;
  state.settings.signalsList = selectedSetting.signalsList;

  setSignalSettingsListFromAPI(state);
};

export const setSettingsFromAPI = (state: EpochState, settings: UserResponse) => {
  const selectedSetting = settings.presets.find(({ id }) => id === settings.selectedPresetId);

  const nextSettings: EpochSettings = {
    isLoading: false,
    signalsList: signalsListFromApi(selectedSetting?.signalsList),
    hypnogramFullNightSignals: signalsListFromApi(selectedSetting?.hypnogramFullNightSignals),
    signalOverflow: (selectedSetting?.signalOverflow as SignalOverflow) ?? SignalOverflow.None,
    presets: presetsFromApi(settings.presets),
    selectedPresetId: selectedSetting?.id ?? settings.selectedPresetId,
    band: selectedSetting?.band ?? true,
    notch: selectedSetting?.notch ?? true,
    viewMode: (selectedSetting?.viewMode as ViewMode) ?? ViewMode.Single,
    artifactColor: selectedSetting?.artifactColor ?? '#FF9999',
    shortcutKeys: selectedSetting?.shortcutKeys ?? [],
    dualTimeBase: {
      topWindowSize: selectedSetting?.dualTimeBase?.topWindowSize ?? 30,
      bottomWindowSize: selectedSetting?.dualTimeBase?.bottomWindowSize ?? 300,
      singleHypnogram: selectedSetting?.dualTimeBase?.singleHypnogram ?? false
    }
  };

  Object.assign(state.settings, nextSettings);

  selectedSetting?.signalsList?.forEach((signal) => {
    state.graph.overflowVisible[signal.name] = signal.isOverflowing ?? false;
  });

  setSignalSettingsListFromAPI(state);
  state.settings.isLoading = false;
};

export const updateSignalVisiblityByName = (
  state: EpochState,
  { signalName, value }: { signalName: string; value: boolean }
) => {
  const signalIndex = state.settings.signalsList.findIndex((signal) => signal.name === signalName);

  if (signalIndex === -1) return;

  state.settings.signalsList[signalIndex].visible = value;
};

export const setSignalOverflow = (
  state: EpochState,
  { signal, value }: { signal: string; value: boolean }
) => {
  state.graph.overflowVisible[signal] = value;
};

export const setAllSignalsOverflow = (state: EpochState, value: boolean) => {
  Object.keys(state.epochData.data?.mapping ?? {}).forEach((signal) => {
    state.graph.overflowVisible[signal] = value;
  });
};

export const setGranularOffset = (state: EpochState, offsetSec: number) => {
  state.granularOffsetSec = offsetSec;
};

export const setPendingGranularOffset = (state: EpochState, offsetSec: number) => {
  state.pendingGranularOffsetSec = offsetSec;
};

export const updateSignalInversion = (
  state: EpochState,
  { signal, ecgPolarityInversion }: { signal: string; ecgPolarityInversion: boolean }
) => {
  // Safely update mapping
  if (state.epochData?.data?.mapping?.[signal]) {
    const mapping = state.epochData.data.mapping[signal] as any;
    mapping.ecgPolarityInversion = ecgPolarityInversion;
  }

  // Optional: keep zoom mapping in sync too
  if (state.graph?.zoom?.[signal]) {
    const zoom = state.graph.zoom[signal] as any;
    zoom.ecgPolarityInversion = ecgPolarityInversion;
  }
};

export const setSignalPolarityInversion = (
  state: EpochState,
  { signal, value }: { signal: string; value: boolean }
) => {
  state.graph.polarityInversion[signal] = value;
};

export const setDualWindowSize = (
  state: EpochState,
  { panel, size }: { panel: SignalPanel; size: number }
) => {
  state.settings.dualTimeBase = {
    ...state.settings.dualTimeBase,
    [panel === SignalPanel.Top ? 'topWindowSize' : 'bottomWindowSize']: size
  };
};

export const setSignalLaneLocalHeights = (
  state: EpochState,
  newLaneHeights: Record<string, number>
) => {
  state.settings.signalsList = state.settings.signalsList.map((signal) => {
    const localValue = newLaneHeights[signal.name];
    if (localValue === undefined) return signal;
    return {
      ...signal,
      signalLaneHeight: { ...signal.signalLaneHeight, localValue }
    };
  });
};
export const resetSignalHeightsFromPreset = (state: EpochState) => {
  const selectedPreset = state.settings.presets.find(
    (preset) => preset.id === state.settings.selectedPresetId
  );

  if (!selectedPreset) return;

  state.settings.signalsList = state.settings.signalsList.map((signal) => {
    const presetSignal = selectedPreset.signalsList.find((s) => s.name === signal.name);
    const presetValue = presetSignal?.signalLaneHeight?.presetValue;

    return {
      ...signal,
      signalLaneHeight: {
        type: presetValue ? 'fixed' : 'auto',
        presetValue,
        localValue: presetValue
      }
    };
  });
};
